import { Button } from "@/components/ui/button";
import { Zap, Shield, ArrowRight, Users } from "lucide-react";
import { motion } from "framer-motion";

import heroDashboard from "@/assets/hero-dashboard.png";

const HeroSection = () => {
  return (
    <section 
      id="hero" 
      className="relative min-h-screen flex items-center pt-20 md:pt-24 pb-12 md:pb-16 overflow-hidden"
      aria-labelledby="hero-heading"
    >
      {/* Background Effects */}
      <div className="absolute inset-0 bg-gradient-to-b from-electric-purple/5 via-transparent to-cyber-cyan/5" aria-hidden="true" />
      <div className="absolute top-1/4 left-1/4 w-64 md:w-96 h-64 md:h-96 bg-electric-purple/20 rounded-full blur-[100px] md:blur-[120px]" aria-hidden="true" />
      <div className="absolute bottom-1/4 right-1/4 w-64 md:w-96 h-64 md:h-96 bg-cyber-cyan/20 rounded-full blur-[100px] md:blur-[120px]" aria-hidden="true" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left Column - Text */}
          <div className="text-center lg:text-left space-y-5 md:space-y-6">
            {/* Urgency Badge */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 px-3 md:px-4 py-2 rounded-full glass-card border-electric-purple/50 bg-electric-purple/10"
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-electric-purple opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-electric-purple"></span>
              </span>
              <span className="text-xs md:text-sm text-electric-purple font-medium">
                🔥 Últimas vagas com desconto este mês
              </span>
            </motion.div>

            {/* Trust Badge */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.05 }}
              className="inline-flex items-center gap-2 px-3 md:px-4 py-2 rounded-full glass-card border-cyber-cyan/30 ml-0 sm:ml-3"
            >
              <span className="w-2 h-2 bg-success-green rounded-full animate-pulse" />
              <span className="text-xs md:text-sm text-muted-foreground">
                +500 empresas já escalam com Zavvi
              </span>
            </motion.div>

            {/* Headline */}
            <motion.h1 
              id="hero-heading"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold leading-tight"
            >
              Gestão de{" "}
              <span className="text-gradient-zavvi">
                Atendimento
              </span>{" "}
              ao Cliente
            </motion.h1>

            {/* Subheadline */}
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-base md:text-lg lg:text-xl text-body-text max-w-xl mx-auto lg:mx-0"
            >
              A única plataforma que une a <strong className="text-cyber-cyan">agilidade do QR Code</strong> com a{" "}
              <strong className="text-electric-purple">blindagem da API Oficial Meta</strong>. 
              Conecte números de WhatsApp ilimitados e centralize seu atendimento para converter mais.
            </motion.p>

            {/* Key Benefits */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="flex flex-wrap justify-center lg:justify-start gap-3 md:gap-4"
            >
              <div className="flex items-center gap-2 text-cyber-cyan">
                <Zap size={16} className="fill-current" />
                <span className="text-xs md:text-sm font-medium">WhatsApps Ilimitados</span>
              </div>
              <div className="flex items-center gap-2 text-electric-purple">
                <Shield size={16} className="fill-current" />
                <span className="text-xs md:text-sm font-medium">Zero Risco de Ban</span>
              </div>
              <div className="flex items-center gap-2 text-success-green">
                <Users size={16} />
                <span className="text-xs md:text-sm font-medium">Equipe Ilimitada</span>
              </div>
            </motion.div>

            {/* CTAs */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center lg:justify-start"
            >
            <Button 
              variant="light" 
              size="lg" 
              className="w-full sm:w-auto min-h-[48px]"
              onClick={() => document.getElementById('planos')?.scrollIntoView({ behavior: 'smooth' })}
              aria-label="Ver planos disponíveis e preços"
            >
              Ver Planos
              <ArrowRight size={18} aria-hidden="true" />
            </Button>
            </motion.div>

            {/* Trust Badges */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="flex items-center gap-4 md:gap-6 justify-center lg:justify-start pt-2 md:pt-4"
            >
              <div className="text-center">
                <div className="text-xl md:text-2xl font-bold text-foreground">15K+</div>
                <div className="text-[10px] md:text-xs text-muted-foreground">Atendimentos/dia</div>
              </div>
              <div className="w-px h-8 md:h-10 bg-border" />
              <div className="text-center">
                <div className="text-xl md:text-2xl font-bold text-foreground">99.9%</div>
                <div className="text-[10px] md:text-xs text-muted-foreground">Uptime</div>
              </div>
              <div className="w-px h-8 md:h-10 bg-border" />
              <div className="text-center">
                <div className="text-xl md:text-2xl font-bold text-foreground">4.9/5</div>
                <div className="text-[10px] md:text-xs text-muted-foreground">Satisfação</div>
              </div>
            </motion.div>
          </div>

          {/* Right Column - Hero Image */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="relative mt-8 lg:mt-0"
          >
            <div className="relative">
              <motion.img 
                src={heroDashboard} 
                alt="Dashboard Zavvi.ai mostrando painel de gestão de atendimento ao cliente via WhatsApp com métricas em tempo real, conversas centralizadas e integração com múltiplos números"
                className="w-full rounded-2xl shadow-xl"
                width={800}
                height={600}
                loading="eager"
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              />
              {/* Floating Elements */}
              <motion.div 
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.6 }}
                className="absolute -top-2 md:-top-4 -right-2 md:-right-4 glass-card px-3 md:px-4 py-1.5 md:py-2 rounded-lg border border-primary/30 shadow-lg bg-background"
              >
                <div className="flex items-center gap-2">
                  <Zap size={14} className="text-primary" />
                  <span className="text-xs md:text-sm font-medium">Conectado</span>
                </div>
              </motion.div>
              <motion.div 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.7 }}
                className="absolute -bottom-2 md:-bottom-4 -left-2 md:-left-4 glass-card px-3 md:px-4 py-1.5 md:py-2 rounded-lg border border-secondary/30 shadow-lg bg-background"
              >
                <div className="flex items-center gap-2">
                  <Shield size={14} className="text-secondary" />
                  <span className="text-xs md:text-sm font-medium">Blindado</span>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
